import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingCart, Plus, Trash, PencilSimple, Package, Star, ChatCircle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

import { API_URL } from '../config';

const StarRating = ({ rating, onRate, interactive = false, size = 18 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        disabled={!interactive}
        onClick={() => interactive && onRate?.(star)}
        className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
        data-testid={`star-${star}`}
      >
        <Star
          size={size}
          weight={star <= rating ? 'fill' : 'regular'}
          className={star <= rating ? 'text-[#FF9500]' : 'text-[#5C6B82]'}
        />
      </button>
    ))}
  </div>
);

const StorePage = () => {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewsDialog, setReviewsDialog] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewsData, setReviewsData] = useState({});
  const [userPurchases, setUserPurchases] = useState([]);
  const [formData, setFormData] = useState({
    title: '', description: '', price: '', category: 'ebook',
    stripe_payment_link: '', file_url: '', image_url: ''
  });

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products`);
      setProducts(res.data);
      // Fetch reviews for all products in parallel
      const reviewPromises = res.data.map(p =>
        axios.get(`${API_URL}/api/products/${p.product_id}/reviews`)
          .then(revRes => ({ id: p.product_id, data: revRes.data }))
          .catch(() => ({ id: p.product_id, data: { reviews: [], count: 0, average_rating: 0 } }))
      );
      const reviewResults = await Promise.all(reviewPromises);
      const reviewsMap = {};
      reviewResults.forEach(r => { reviewsMap[r.id] = r.data; });
      setReviewsData(reviewsMap);
    } catch (e) {
      console.error('Error fetching products:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    if (isAuthenticated) {
      axios.get(`${API_URL}/api/purchases`, { withCredentials: true })
        .then(res => setUserPurchases(res.data.map(p => p.product_id)))
        .catch(() => {});
    }
  }, [fetchProducts, isAuthenticated]);

  const hasPurchased = (productId) => userPurchases.includes(productId);

  const hasReviewed = (productId) => {
    const data = reviewsData[productId];
    if (!data) return false;
    return data.reviews.some(r => r.user_email === user?.email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, price: parseFloat(formData.price) };
    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.product_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/products`, payload, { withCredentials: true });
      }
      setShowAddDialog(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (e) {
      console.error('Error saving product:', e);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, { withCredentials: true });
      fetchProducts();
    } catch (e) {
      console.error('Error deleting product:', e);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title, description: product.description,
      price: String(product.price), category: product.category,
      stripe_payment_link: product.stripe_payment_link,
      file_url: product.file_url || '', image_url: product.image_url || ''
    });
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', price: '', category: 'ebook', stripe_payment_link: '', file_url: '', image_url: '' });
  };

  const handleBuy = (product) => {
    window.open(product.stripe_payment_link, '_blank');
  };

  const handleSubmitReview = async () => {
    if (!reviewDialog || reviewRating === 0) return;
    try {
      await axios.post(`${API_URL}/api/products/${reviewDialog}/reviews`, {
        rating: reviewRating, comment: reviewComment
      }, { withCredentials: true });
      setReviewDialog(null);
      setReviewRating(0);
      setReviewComment('');
      fetchProducts();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error submitting review');
    }
  };

  const handleDeleteReview = async (reviewId, productId) => {
    try {
      await axios.delete(`${API_URL}/api/reviews/${reviewId}`, { withCredentials: true });
      fetchProducts();
      // Update the reviews dialog if open
      if (reviewsDialog) {
        const res = await axios.get(`${API_URL}/api/products/${productId}/reviews`);
        setReviewsData(prev => ({ ...prev, [productId]: res.data }));
      }
    } catch (e) {
      console.error('Error deleting review:', e);
    }
  };

  const getCategoryLabel = (cat) => {
    const labels = { ebook: 'eBook', guide: 'Guide', software: 'Software', membership: 'Membership', other: 'Digital Product' };
    return labels[cat] || cat;
  };

  const getCategoryColor = (cat) => {
    const colors = { ebook: '#34C759', guide: '#007AFF', software: '#FF9500', membership: '#AF52DE', other: '#8892B0' };
    return colors[cat] || '#8892B0';
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-black text-3xl uppercase text-white mb-2" data-testid="store-title">
              Digital Store
            </h1>
            <p className="text-[#8892B0]">Money-making digital products and resources</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => { resetForm(); setEditingProduct(null); setShowAddDialog(true); }}
              className="flex items-center gap-2 px-6 py-3 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors"
              data-testid="add-product-btn"
            >
              <Plus size={18} weight="bold" /> Add Product
            </button>
          )}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12 text-[#8892B0]">Loading products...</div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 bg-[#131313] border border-white/5">
            <Package size={48} className="mx-auto text-[#5C6B82] mb-4" />
            <p className="text-[#8892B0] mb-2">No products available yet</p>
            <p className="text-sm text-[#5C6B82]">Check back soon for new offerings</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
            {products.map((product) => {
              const revData = reviewsData[product.product_id];
              const avgRating = revData?.average_rating || 0;
              const reviewCount = revData?.count || 0;

              return (
                <div
                  key={product.product_id}
                  className="bg-[#131313] border border-white/5 hover:border-white/20 transition-colors flex flex-col"
                  data-testid={`product-${product.product_id}`}
                >
                  {/* Product Image */}
                  <div className="h-64 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] flex items-center justify-center border-b border-white/5 overflow-hidden">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.title} className="w-full h-full object-contain bg-[#0A0A0A]" />
                    ) : (
                      <Package size={48} weight="duotone" style={{ color: getCategoryColor(product.category) }} />
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className="px-2 py-0.5 text-xs uppercase tracking-wider font-bold"
                        style={{ backgroundColor: `${getCategoryColor(product.category)}20`, color: getCategoryColor(product.category) }}
                      >
                        {getCategoryLabel(product.category)}
                      </span>
                    </div>
                    <h3 className="text-white font-bold text-lg mb-2">{product.title}</h3>
                    <p className="text-[#8892B0] text-sm mb-3 flex-1 line-clamp-3">{product.description}</p>

                    {/* Rating Display */}
                    <div
                      className="flex items-center gap-2 mb-4 cursor-pointer group"
                      onClick={() => reviewCount > 0 && setReviewsDialog(product.product_id)}
                      data-testid={`rating-display-${product.product_id}`}
                    >
                      <StarRating rating={Math.round(avgRating)} />
                      <span className="text-sm text-[#8892B0] group-hover:text-white transition-colors">
                        {avgRating > 0 ? `${avgRating}` : 'No reviews'}
                        {reviewCount > 0 && ` (${reviewCount})`}
                      </span>
                    </div>

                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-2xl font-black text-white font-mono">${product.price.toFixed(2)}</span>
                      <button
                        onClick={() => handleBuy(product)}
                        className="flex items-center gap-2 px-5 py-2.5 bg-[#007AFF] text-white font-bold uppercase tracking-wider text-sm hover:bg-[#005bb5] transition-colors"
                        data-testid={`buy-${product.product_id}`}
                      >
                        <ShoppingCart size={16} /> Buy Now
                      </button>
                    </div>

                    {/* Write Review Button - only for purchasers who haven't reviewed */}
                    {isAuthenticated && hasPurchased(product.product_id) && !hasReviewed(product.product_id) && (
                      <button
                        onClick={() => { setReviewDialog(product.product_id); setReviewRating(0); setReviewComment(''); }}
                        className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 border border-[#FF9500]/30 text-[#FF9500] font-bold uppercase tracking-wider text-xs hover:bg-[#FF9500]/10 transition-colors"
                        data-testid={`write-review-${product.product_id}`}
                      >
                        <Star size={14} weight="fill" /> Write a Review
                      </button>
                    )}

                    {/* Admin Controls */}
                    {isAdmin && (
                      <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                        <button
                          onClick={() => handleEdit(product)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs text-[#8892B0] hover:text-white border border-white/10 hover:border-white/30 transition-colors"
                          data-testid={`edit-${product.product_id}`}
                        >
                          <PencilSimple size={14} /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(product.product_id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs text-[#FF3B30] hover:text-white border border-[#FF3B30]/30 hover:border-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors"
                          data-testid={`delete-${product.product_id}`}
                        >
                          <Trash size={14} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add/Edit Product Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-[#131313] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-xl uppercase">
                {editingProduct ? 'Edit Product' : 'Add Product'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-[#8892B0]">Title</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="bg-transparent border-white/20 text-white rounded-none mt-1" required data-testid="product-title-input" />
              </div>
              <div>
                <Label className="text-[#8892B0]">Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-transparent border-white/20 text-white rounded-none mt-1" rows={3} required data-testid="product-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#8892B0]">Price ($)</Label>
                  <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="bg-transparent border-white/20 text-white rounded-none mt-1" required data-testid="product-price-input" />
                </div>
                <div>
                  <Label className="text-[#8892B0]">Category</Label>
                  <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                    <SelectTrigger className="bg-transparent border-white/20 text-white rounded-none mt-1" data-testid="product-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      <SelectItem value="ebook">eBook / PDF</SelectItem>
                      <SelectItem value="guide">Guide / Blueprint</SelectItem>
                      <SelectItem value="software">Software / Tool</SelectItem>
                      <SelectItem value="membership">Membership / Access</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[#8892B0]">Stripe Payment Link</Label>
                <Input value={formData.stripe_payment_link} onChange={(e) => setFormData({ ...formData, stripe_payment_link: e.target.value })} placeholder="https://buy.stripe.com/..." className="bg-transparent border-white/20 text-white rounded-none mt-1" required data-testid="product-stripe-link-input" />
              </div>
              <div>
                <Label className="text-[#8892B0]">File/Download URL</Label>
                <Input value={formData.file_url} onChange={(e) => setFormData({ ...formData, file_url: e.target.value })} placeholder="https://..." className="bg-transparent border-white/20 text-white rounded-none mt-1" data-testid="product-file-url-input" />
              </div>
              <div>
                <Label className="text-[#8892B0]">Image URL (optional)</Label>
                <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." className="bg-transparent border-white/20 text-white rounded-none mt-1" data-testid="product-image-url-input" />
              </div>
              <button type="submit" className="w-full py-3 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors" data-testid="product-submit-btn">
                {editingProduct ? 'Update Product' : 'Create Product'}
              </button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Write Review Dialog */}
        <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
          <DialogContent className="bg-[#131313] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-xl uppercase">Write a Review</DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              <div>
                <Label className="text-[#8892B0] block mb-3">Your Rating</Label>
                <StarRating rating={reviewRating} onRate={setReviewRating} interactive size={28} />
              </div>
              <div>
                <Label className="text-[#8892B0]">Comment (optional)</Label>
                <Textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Share your experience with this product..."
                  className="bg-transparent border-white/20 text-white rounded-none mt-1"
                  rows={4}
                  data-testid="review-comment-input"
                />
              </div>
              <button
                onClick={handleSubmitReview}
                disabled={reviewRating === 0}
                className="w-full py-3 bg-[#FF9500] text-black font-bold uppercase tracking-widest hover:bg-[#e08600] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="submit-review-btn"
              >
                Submit Review
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Reviews Dialog */}
        <Dialog open={!!reviewsDialog} onOpenChange={() => setReviewsDialog(null)}>
          <DialogContent className="bg-[#131313] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                <ChatCircle size={22} /> Reviews
                {reviewsDialog && reviewsData[reviewsDialog] && (
                  <span className="text-sm font-normal text-[#8892B0] ml-2">
                    {reviewsData[reviewsDialog].average_rating}/5 ({reviewsData[reviewsDialog].count} reviews)
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2" data-testid="reviews-list">
              {reviewsDialog && reviewsData[reviewsDialog]?.reviews.map((review) => (
                <div key={review.review_id} className="bg-[#0A0A0A] border border-white/5 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <StarRating rating={review.rating} size={14} />
                      <span className="text-white font-bold text-sm">{review.user_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#5C6B82]">
                        {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      {(review.user_email === user?.email || isAdmin) && (
                        <button
                          onClick={() => handleDeleteReview(review.review_id, review.product_id)}
                          className="text-[#FF3B30] hover:text-white transition-colors"
                          data-testid={`delete-review-${review.review_id}`}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  {review.comment && (
                    <p className="text-[#8892B0] text-sm">{review.comment}</p>
                  )}
                </div>
              ))}
              {reviewsDialog && (!reviewsData[reviewsDialog] || reviewsData[reviewsDialog]?.count === 0) && (
                <p className="text-[#5C6B82] text-center py-6">No reviews yet</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default StorePage;
